import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import DateTimePicker from '@/components/UI/DateTimePicker';
import DataTable from '@/components/UI/DataTable';
import { getDateFromUnix, getDatePlusMinus, trimDecimals } from '@einhaus/meteoric-helpers';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { EntityObjectCompressed, EntityTypeUnion, RealTimeEntryFeedRow } from '@einhaus/project314-types';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import EntityLink from '@/components/Entity/EntityLink';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import Button from '@/components/UI/Button.tsx';
import FormSwitch from '@/components/UI/FormSwitch.tsx';
import { trpc } from '@/api/trpc.ts';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useFetchEntities } from '@/hooks/useFetchEntities';
import { usePortfolios } from '@/hooks/usePortfolios';
import { summarizeLegUnderlyingBias } from '@einhaus/project314-shared';
import { formatPositionSide, formatUnderlyingBias, type UnderlyingBiasSummary } from '@/utils/tradeSemantics';

type RealTimeEntry = Omit<RealTimeEntryFeedRow, 'valid' | 'scoringAudit'> & {
    valid: boolean;
    statusLabel: string;
    statusCategory: 'success' | 'warning' | 'error' | 'info';
    statusOrder: number;
    auditScope: string | null;
    predictedProfit: number | null;
    baselineReturn: number | null;
    confidence: number | null;
    matchedRules: number | null;
    sizingMultiplier: number | null;
    scoreDecision: 'accept' | 'reject' | null;
    scoreRejectionReason: string | null;
    entityNotInExperiment: boolean;
    hasScoringAudit: boolean;
    priceExecution: {
        decisionId: number;
        targetPrice: number;
        targetRevision: number;
        targetSourceIdentity: string;
        eligibleAt: number;
        expiresAt: number;
        status: string;
        reason: string | null;
        executions: {
            id: number;
            portfolioId: number;
            status: string;
            reason: string | null;
            brokerageOrderStatus: string | null;
        }[];
    } | null;
    entityName?: string;
    entitySymbol?: string;
    strategyName?: string;
    entryLogLink?: string;
    underlyingBiasSummary: UnderlyingBiasSummary;
};

const RealTimeEntries: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    const [showValidOnly, setShowValidOnly] = useState<boolean>(false);

    // Show loading spinner when component mounts
    useEffect(() => {
        showSpinner();

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, []);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    // Get query parameters
    const portfolioIdParam = queryParams.get('portfolioId') ? Number(queryParams.get('portfolioId')) : null;
    const entityIdParam = queryParams.get('entityId') ? Number(queryParams.get('entityId')) : null;
    const entityTypeParam = queryParams.get('entityType') as EntityTypeUnion['entityType'] | null;
    const strategyIdParam = queryParams.get('strategyId') ? Number(queryParams.get('strategyId')) : null;

    // Date range state
    const defaultStartDate = getDatePlusMinus('-', 1, 'day', 'ymdhms');
    const defaultEndDate = getDatePlusMinus('+', 1, 'day', 'ymdhms');
    const [startDateTime, setStartDateTime] = useState<string>(defaultStartDate);
    const [endDateTime, setEndDateTime] = useState<string>(defaultEndDate);

    const getStatusBadgeClasses = (category: RealTimeEntry['statusCategory']) => {
        switch (category) {
            case 'success':
                return 'bg-green-900/40 text-green-200 border border-green-600';
            case 'warning':
                return 'bg-yellow-900/40 text-yellow-200 border border-yellow-500';
            case 'error':
                return 'bg-red-900/40 text-red-200 border border-red-600';
            case 'info':
                return 'bg-slate-800 text-slate-100 border border-slate-600';
            default:
                return 'bg-slate-800 text-slate-100 border border-slate-600';
        }
    };

    const formatReason = (entry: RealTimeEntry) => {
        const fallback = entry.entityNotInExperiment ? 'Entity not in experiment baseline' : '';
        const reason = entry.statusReasonDetail || entry.statusReasonCode || entry.scoreRejectionReason || fallback;

        if (!reason) return '—';
        return <span>{reason}</span>;
    };

    const formatDecisionSizing = (entry: RealTimeEntry) => {
        const pieces: string[] = [];
        if (entry.scoreDecision) pieces.push(entry.scoreDecision === 'accept' ? 'Accept' : 'Reject');
        if (entry.sizingMultiplier !== null && entry.sizingMultiplier !== undefined) pieces.push(`x${entry.sizingMultiplier.toFixed(2)}`);
        return pieces.length > 0 ? pieces.join(' • ') : '—';
    };

    const formatConfidenceRules = (entry: RealTimeEntry) => {
        const parts: string[] = [];
        if (entry.confidence !== null && entry.confidence !== undefined) parts.push(`${Math.round(entry.confidence * 100)}%`);
        if (entry.matchedRules !== null && entry.matchedRules !== undefined) parts.push(`${entry.matchedRules} rules`);
        return parts.length > 0 ? parts.join(' • ') : '—';
    };

    const formatPredictedProfit = (value: number | null) => {
        if (value === null || value === undefined) return '—';
        const textColor = value >= 0 ? 'text-green-400' : 'text-red-400';
        return <span className={`font-semibold ${textColor}`}>{value.toFixed(2)}%</span>;
    };

    // Table columns
    const columns = React.useMemo<ColumnDef<RealTimeEntry>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                size: 65,
                meta: { className: 'text-xs text-zinc-400 whitespace-nowrap' },
                cell: (info) => {
                    const entry = info.row.original;
                    return entry.feedSource === 'screenerGating' ? `S${entry.id}` : entry.id;
                }
            },
            {
                // Use accessor function to return a sortable/searchable string
                accessorFn: (row) => {
                    if (row.entitySymbol && row.entityName) {
                        return `${row.entitySymbol} ${row.entityName}`;
                    } else if (row.entitySymbol) {
                        return row.entitySymbol;
                    } else if (row.entityName) {
                        return row.entityName;
                    }

                    return '';
                },
                id: 'entity', // Use id instead of accessorKey when using accessorFn
                header: 'Entity',
                size: 180,
                meta: { className: 'text-sm' },
                cell: (info) => {
                    const entry = info.row.original;

                    if (entry.entityName && entry.entitySymbol && entry.entityType) {
                        return (
                            <EntityLink
                                showName
                                showSymbol
                                entity={{
                                    entityId: entry.entityId,
                                    entityType: entry.entityType,
                                    entityName: entry.entityName,
                                    entitySymbol: entry.entitySymbol
                                }}
                            />
                        );
                    }

                    return '';
                }
            },
            {
                accessorKey: 'direction',
                header: 'Position side',
                size: 105,
                meta: { className: 'text-sm' },
                cell: (info) => {
                    const direction = info.row.original.direction;
                    if (!direction) return <span className="text-zinc-500">—</span>;

                    return <span className="font-semibold text-zinc-200">{formatPositionSide(direction)}</span>;
                }
            },
            {
                accessorKey: 'underlyingBiasSummary',
                header: 'Underlying exposure',
                size: 145,
                meta: { className: 'text-sm' },
                cell: ({ row }) => formatUnderlyingBias(row.original.underlyingBiasSummary)
            },
            {
                id: 'strategy',
                header: 'Strategy',
                size: 300,
                accessorFn: (row) => (row.strategyName ? `${row.strategyName} ${row.strategyId}` : String(row.strategyId)),
                meta: { className: 'text-sm' },
                cell: (info) => {
                    const entry = info.row.original;
                    return (
                        <Link className="text-primary hover:text-blue-400" to={`/user/tradingStrategy/${entry.strategyId}`}>
                            {entry.strategyName || 'Strategy'}
                            <span className="block text-xs text-zinc-400">#{entry.strategyId}</span>
                        </Link>
                    );
                }
            },
            {
                accessorKey: 'statusLabel',
                header: 'Status',
                size: 130,
                cell: (info) => {
                    const entry = info.row.original;
                    return (
                        <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusBadgeClasses(
                                entry.statusCategory
                            )}`}
                        >
                            {entry.statusLabel}
                        </span>
                    );
                }
            },
            {
                id: 'priceExecution',
                header: 'Price execution',
                size: 240,
                accessorFn: (row) => row.priceExecution?.targetPrice ?? null,
                meta: { className: 'text-xs text-zinc-300' },
                cell: (info) => {
                    const execution = info.row.original.priceExecution;
                    if (!execution) return <span className="text-zinc-500">—</span>;
                    const executionStates = execution.executions.map((item) => item.brokerageOrderStatus ?? item.status);
                    return (
                        <div className="space-y-0.5">
                            <div className="font-semibold text-amber-200">
                                ${trimDecimals(execution.targetPrice)} · {execution.status}
                            </div>
                            <div>Eligible {getDateFromUnix(execution.eligibleAt, 'ymdhm', 'America/New_York')} ET</div>
                            <div>Expires {getDateFromUnix(execution.expiresAt, 'ymdhm', 'America/New_York')} ET</div>
                            <div className="max-w-56 truncate text-zinc-500" title={execution.targetSourceIdentity}>
                                {execution.targetSourceIdentity}
                            </div>
                            {executionStates.length > 0 && <div>Orders: {executionStates.join(', ')}</div>}
                        </div>
                    );
                }
            },
            {
                id: 'reason',
                header: 'Reason',
                size: 260,
                accessorFn: (row) =>
                    row.statusReasonDetail ||
                    row.statusReasonCode ||
                    row.scoreRejectionReason ||
                    (row.entityNotInExperiment ? 'Entity not in experiment baseline' : ''),
                meta: { className: 'text-sm text-zinc-300' },
                cell: (info) => {
                    const entry = info.row.original;
                    return formatReason(entry);
                }
            },
            {
                id: 'predictedProfit',
                header: 'Pred. Profit',
                size: 100,
                accessorFn: (row) => row.predictedProfit ?? null,
                meta: { className: 'text-sm whitespace-nowrap tabular-nums' },
                cell: (info) => formatPredictedProfit(info.row.original.predictedProfit)
            },
            {
                id: 'confidence',
                header: 'Confidence / Rules',
                size: 120,
                accessorFn: (row) => row.confidence ?? null,
                meta: { className: 'text-sm whitespace-nowrap tabular-nums' },
                cell: (info) => formatConfidenceRules(info.row.original)
            },
            {
                id: 'sizingMultiplier',
                header: 'Decision / Sizing',
                size: 120,
                accessorFn: (row) => row.sizingMultiplier ?? row.scoreDecision ?? null,
                meta: { className: 'text-sm whitespace-nowrap tabular-nums' },
                cell: (info) => formatDecisionSizing(info.row.original)
            },
            {
                accessorKey: 'valid',
                header: 'Score Valid',
                size: 75,
                meta: { className: 'text-sm' },
                cell: (info) => {
                    const entry = info.row.original;
                    if (entry.feedSource === 'screenerGating') return <span className="text-zinc-500">—</span>;
                    return entry.valid ? <span className="text-green-400">Yes</span> : <span className="text-zinc-400">No</span>;
                }
            },
            {
                accessorKey: 'entryDateTime',
                header: 'Datetime',
                size: 145,
                meta: { className: 'text-xs text-zinc-400 whitespace-nowrap tabular-nums' }
            },
            {
                accessorKey: 'entryLogLink',
                header: 'Entry Log',
                size: 105,
                cell: (info) => {
                    const entry = info.row.original;

                    if (entry.valid && entry.entryLogLink) {
                        return (
                            <Button className="whitespace-nowrap" size="sm" to={entry.entryLogLink} variant="dark">
                                Entry Log
                            </Button>
                        );
                    }

                    return '';
                }
            }
        ],
        []
    );

    // Fetch real-time entries with tRPC
    const realTimeEntriesQuery = useAppQuery(
        {
            ...trpc.user.fetchRealTimeEntries.queryOptions({
                entityId: entityIdParam,
                entityType: entityTypeParam,
                strategyId: strategyIdParam,
                startDateTime,
                endDateTime,
                portfolioId: portfolioIdParam
            }),
            enabled: true
        },
        {
            fallbackErrorMessage: 'Failed to load real-time entries',
            setError: (msg) => setError(msg)
        }
    );

    // Fetch portfolios with tRPC
    const portfoliosQuery = usePortfolios({
        errorConfig: {
            fallbackErrorMessage: 'Failed to load portfolios',
            setError: (msg) => setError(msg)
        }
    });

    const { portfolios } = portfoliosQuery;

    // Fetch strategies with tRPC
    const strategiesQuery = useAppQuery(
        {
            ...trpc.strategy.fetchStrategies.queryOptions({ ids: [], requireActive: false }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load strategies',
            setError: (msg) => setError(msg)
        }
    );

    // Extract data from queries
    const realTimeEntriesData = useMemo(() => realTimeEntriesQuery.data?.entries || [], [realTimeEntriesQuery.data]);
    const strategies = useMemo(() => strategiesQuery.data?.strategies || [], [strategiesQuery.data]);

    // Create a map of entity IDs and types to fetch
    const entitiesToFetch = useMemo(() => {
        // Use a Map to ensure uniqueness based on entity ID and type
        const entitiesMap = new Map<string, EntityObjectCompressed>();

        // Fetch entities for the entries
        realTimeEntriesData.forEach((entry) => {
            // Create a unique key for each entity
            const entityKey = `${entry.entityId}_${entry.entityType}`;

            // Only add if not already in the map
            if (!entitiesMap.has(entityKey)) {
                entitiesMap.set(entityKey, {
                    i: entry.entityId,
                    t: entry.entityType === 'Asset' ? 'a' : entry.entityType === 'Cryptocurrency' ? 'c' : 'i'
                });
            }
        });

        // Convert Map values to an array for the API call
        return Array.from(entitiesMap.values());
    }, [realTimeEntriesData]);

    // Fetch entities using the batched hook
    const { entities: entitiesData, isLoading: entitiesLoading } = useFetchEntities(entitiesToFetch);

    // Prepare enriched entries with entity and strategy information
    const entries = useMemo(() => {
        if (realTimeEntriesData.length === 0 || (entitiesToFetch.length > 0 && entitiesData.length === 0)) {
            return [];
        }

        return realTimeEntriesData.map((entry) => {
            const entity = entitiesData.find((e) => e.id === entry.entityId && e.entityType === entry.entityType);
            const strategy = strategies.find((s) => s.id === entry.strategyId);

            // Create entry log link
            let entryLogLinkParams = `?startDateTime=${entry.entryDateTime}&endDateTime=${getDatePlusMinus(
                '+',
                90,
                'second',
                'ymdhms',
                null,
                entry.entryDateTime
            )}`;

            if (portfolioIdParam) entryLogLinkParams += `&portfolioId=${portfolioIdParam}`;
            entryLogLinkParams += `&entityId=${entry.entityId}`;
            entryLogLinkParams += `&entityType=${entry.entityType}`;
            if (strategyIdParam) entryLogLinkParams += `&strategyId=${strategyIdParam}`;

            const entryLogLink = `/user/entryLog/${entryLogLinkParams}`;

            return {
                ...entry,
                entityName: entity?.name ?? undefined,
                entitySymbol: entity?.symbol,
                strategyName: strategy?.name || '',
                underlyingBiasSummary: strategy ? summarizeLegUnderlyingBias(strategy.legs) : 'unknown',
                entryLogLink: entry.valid ? entryLogLink : ''
            };
        });
    }, [realTimeEntriesData, entitiesData, strategies, entitiesToFetch.length, portfolioIdParam, strategyIdParam]);

    // Manage loading state
    const loading = useMemo(() => {
        return (
            realTimeEntriesQuery.isLoading ||
            portfoliosQuery.isLoading ||
            strategiesQuery.isLoading ||
            (entitiesToFetch.length > 0 && entitiesLoading)
        );
    }, [realTimeEntriesQuery.isLoading, portfoliosQuery.isLoading, strategiesQuery.isLoading, entitiesLoading, entitiesToFetch.length]);

    // Show/hide spinner based on loading state
    useEffect(() => {
        if (loading) {
            showSpinner();
        } else {
            hideSpinner();
        }
    }, [loading]);

    // Handle date range changes
    const handleDateRangeChange = (start: string, end: string) => {
        // Show spinner while updating
        showSpinner();

        // Update date range state
        setStartDateTime(start);
        setEndDateTime(end);

        // The queries will automatically refetch when startDateTime or endDateTime change
        // because they are used in the query parameters

        // Hide spinner after a short delay to allow queries to start
        setTimeout(() => {
            hideSpinner();
        }, 100);
    };

    // Filter entries based on showValidOnly state
    const filteredEntries = useMemo(() => {
        if (showValidOnly) {
            return entries.filter((entry) => entry.valid);
        }

        return entries;
    }, [entries, showValidOnly]);

    return (
        <UserLayout containerSize="1600px" title="Real Time Entries - Project 314">
            <div className="container mx-auto px-4">
                <div className="flex justify-between mt-4">
                    <div>
                        <h3 className="text-white text-xl font-semibold mb-4" id="realTimeEntriesHeader">
                            Real Time Entries
                            {portfolioIdParam &&
                                portfolios.length > 0 &&
                                (() => {
                                    const portfolio = portfolios.find((p) => p.id === portfolioIdParam);

                                    if (portfolio) {
                                        return (
                                            <>
                                                {' '}
                                                -{' '}
                                                <Link className="text-primary hover:text-blue-400" to={`/user/portfolio/${portfolio.id}/`}>
                                                    {portfolio.name}
                                                </Link>{' '}
                                                ({portfolio.type})
                                            </>
                                        );
                                    }

                                    return null;
                                })()}
                        </h3>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md" role="alert">
                        {error}
                    </div>
                )}

                <div className="mb-6">
                    <DateTimePicker
                        className="mb-4"
                        endDateTime={endDateTime}
                        startDateTime={startDateTime}
                        onDateRangeChange={handleDateRangeChange}
                    />
                    <div className="mt-4">
                        <FormSwitch
                            checked={showValidOnly}
                            label="Show valid entries only"
                            onChange={(e) => setShowValidOnly(e.target.checked)}
                        />
                    </div>
                </div>

                {loading ? (
                    <DataLoadingSpinner />
                ) : (
                    <DataTable search sort striped columns={columns} data={filteredEntries} pagination={{ enabled: true, pageSize: 10 }} />
                )}
            </div>
        </UserLayout>
    );
};

export default RealTimeEntries;
